import { authenticatedFetch } from "./api";
import { isTenantOverridden } from "./activeTenant";
import { SubscriptionStatus } from "../types";

// ENG-2387 — what the console is allowed to tell the extension.
//
// ## The bug this exists to prevent
//
// The console pushes subscription state to the extension over chrome.runtime
// (`auth-complete` on load and on the 45-minute token refresh, `subscription-updated`
// whenever subscriptionStatus changes). The extension drives the Shield off it: the
// tenant extension whitelist and, via /my-categories, the protected sites.
//
// Once the console can switch tenants, its own /subscription-status describes the
// SELECTED tenant. Pushing that to the extension would mean: an MSP tech opens client
// tenant "Globex" in the console, and their own browser's Shield adopts Globex's
// whitelist and protected sites. That is exactly the failure ENG-2409 chose
// caller-scoping to prevent — the backend persists nothing, so the leak could only
// ever come from the console handing the extension the wrong thing.
//
// ## The rule
//
// Extension-bound subscription state is always the OPERATOR'S OWN, never the selected
// tenant's. When a switch is in effect we re-fetch unscoped; otherwise the console's
// own copy already is the operator's own and no extra call happens.
//
// The extension itself needs no change: it polls the backend with no active-tenant
// header, so it already resolves the operator's real tenant. It doesn't need the
// console's help — it needs the console not to lie to it.

/**
 * ENG-3461 — the account a status was fetched for. It rides on every status the extension is sent
 * (`fetchedFor`), so the extension can refuse a plan that is not the signed-in account's instead of
 * stamping it with whoever is signed in when it arrives. Stamped where the status is FETCHED, not
 * where it is sent: a send-time stamp would name whoever is signed in at the send, which is exactly
 * the mistake the extension used to make.
 *
 * An extension from before ENG-3461 ignores the field (it stamps its own owner over the plan), so
 * adding it changes nothing there.
 */
export interface PlanOwner {
  uid: string;
  email: string | null;
}

export type ExtensionSubscription = SubscriptionStatus & { fetchedFor: PlanOwner };

/**
 * The subscription status safe to send to the extension.
 *
 * @param consoleStatus what the console fetched (scoped to the selected tenant).
 * @param fetchedFor the account `consoleStatus` was fetched for (ENG-3461).
 * @returns the operator's own status, stamped with `fetchedFor`; a copy of `consoleStatus` when no
 *   switch is active.
 */
export async function resolveExtensionSubscription(
  consoleStatus: SubscriptionStatus | null,
  fetchedFor: PlanOwner,
): Promise<ExtensionSubscription | null> {
  const own = await resolveOwnSubscription(consoleStatus);
  return own ? { ...own, fetchedFor: { uid: fetchedFor.uid, email: fetchedFor.email } } : null;
}

async function resolveOwnSubscription(
  consoleStatus: SubscriptionStatus | null,
): Promise<SubscriptionStatus | null> {
  if (!isTenantOverridden()) return consoleStatus;
  try {
    const response = await authenticatedFetch("/subscription-status", {}, { scoped: false });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    // Fail closed: send nothing rather than another tenant's policy. The extension
    // keeps whatever it already had and re-resolves on its own next poll.
    return null;
  }
}
